import { ProspectDataDto, ProspectDataExperienceDto, ProspectLinkedinProfileVerifiedStatus } from '@zaplify/prospects';

export class LinkedinPhoneNumberDto {
    number: string;
    type?: string;

    constructor(number: string, type?: string) {
        this.number = number;
        this.type = type;
    }
}

export class LinkedinProfileEducationDto {
    schoolName: string;
    schoolLinkedinUrl: string;
    schoolDomain: string;
    majors: string[];
    startDate: string;

    constructor(
        schoolName: string,
        schoolLinkedinUrl: string,
        schoolDomain: string,
        majors: string[],
        startDate: string,
    ) {
        this.schoolName = schoolName;
        this.schoolLinkedinUrl = schoolLinkedinUrl;
        this.schoolDomain = schoolDomain;
        this.majors = majors;
        this.startDate = startDate;
    }
}

export class LinkedinProfileExperienceDto {
    isCurrent: boolean;
    summary?: string;
    companyName: string;
    companyWebsite?: string;
    companyId?: string;
    companyPublicIdentifier?: string;
    companyIndustry?: string;
    companySizeRange?: string;
    companyImageUrl?: string;
    companyDescription?: string;
    jobTitle?: string;
    jobStartDate?: string;
    jobEndDate?: string;

    constructor(experience: LinkedinProfileExperienceDto) {
        this.isCurrent = experience.isCurrent;
        this.summary = experience.summary;
        this.companyName = experience.companyName;
        this.companyWebsite = experience.companyWebsite;
        this.companyId = experience.companyId;
        this.companyPublicIdentifier = experience.companyPublicIdentifier;
        this.companyIndustry = experience.companyIndustry;
        this.companySizeRange = experience.companySizeRange;
        this.companyImageUrl = experience.companyImageUrl;
        this.jobTitle = experience.jobTitle;
        this.jobStartDate = experience.jobStartDate;
        this.jobEndDate = experience.jobEndDate;
    }

    static fromProspectDataExperienceDto(experience: ProspectDataExperienceDto): LinkedinProfileExperienceDto {
        return new LinkedinProfileExperienceDto({
            isCurrent: false,
            summary: experience.occupationDescription as string,
            companyName: experience.organizationName as string,
            companyWebsite: experience.organizationDomain as string,
            companyId: experience.organizationLinkedinId as string,
            companyPublicIdentifier: experience.organizationLinkedinUrl
                ? extractPublicIdentifierFromLinkedinProfileUrl(experience.organizationLinkedinUrl)
                : undefined,
            companyIndustry: experience.organizationIndustry as string,
            companySizeRange: experience.organizationSize as string,
            companyImageUrl: experience.organizationImageUrl as string,
            companyDescription: experience.organizationDescription as string,
            jobTitle: experience.occupationTitle as string,
            jobStartDate: experience.occupationStartDate as string,
            jobEndDate: experience.occupationEndDate as string,
        });
    }
}

export class LinkedinProfileSummaryDto {
    memberId: string;
    publicIdentifier: string;
    fullName: string;
    profileImageUrl?: string;
    numberOfConnections?: number;
    headline?: string;
    locationString?: string;
    personalSummary?: string;

    constructor(linkedinProfile: LinkedinProfileDto) {
        this.memberId = linkedinProfile.memberId;
        this.publicIdentifier = linkedinProfile.publicIdentifier;
        this.fullName = linkedinProfile.fullName;
        this.profileImageUrl = linkedinProfile.profileImageUrl;
        this.numberOfConnections = linkedinProfile.numberOfConnections;
        this.headline = linkedinProfile.headline;
        this.locationString = linkedinProfile.locationString;
        this.personalSummary = linkedinProfile.personalSummary;
    }
}

export class LinkedinProfileDto {
    memberId: string;
    memberNumberId?: string;
    publicIdentifier: string;

    firstName: string;
    lastName?: string;
    fullName: string;
    profileImageUrl?: string;
    numberOfConnections?: number;
    headline?: string;
    personalSummary?: string;
    locationString?: string;
    email?: string;
    phoneNumbers?: LinkedinPhoneNumberDto[];
    interests?: string[];
    skills?: string[];
    education?: LinkedinProfileEducationDto[];
    experiences?: LinkedinProfileExperienceDto[];
    personalIndustry?: string;
    lastChecked: Date;

    constructor(linkedinProfile: LinkedinProfileDto) {
        this.memberId = linkedinProfile.memberId;
        this.memberNumberId = linkedinProfile.memberNumberId;
        this.publicIdentifier = linkedinProfile.publicIdentifier;
        this.firstName = linkedinProfile.firstName;
        this.lastName = linkedinProfile.lastName;
        this.fullName = linkedinProfile.fullName;
        this.profileImageUrl = linkedinProfile.profileImageUrl;
        this.numberOfConnections = linkedinProfile.numberOfConnections;
        this.headline = linkedinProfile.headline;
        this.personalSummary = linkedinProfile.personalSummary;
        this.locationString = linkedinProfile.locationString;
        this.email = linkedinProfile.email;
        this.phoneNumbers = linkedinProfile.phoneNumbers;
        this.interests = linkedinProfile.interests;
        this.skills = linkedinProfile.skills;
        this.education = linkedinProfile.education;
        this.experiences = linkedinProfile.experiences;
        this.personalIndustry = linkedinProfile.personalIndustry;
        this.lastChecked = linkedinProfile.lastChecked;
    }

    static isProfilePartial(profile: LinkedinProfileDto): boolean {
        /**
         * A partial profile only contains name, memberId, headline, profileImageUrl and lastChecked
         */
        return (
            !profile.experiences?.length &&
            !profile.education?.length &&
            !profile.personalSummary &&
            !profile.numberOfConnections &&
            !profile.locationString
        );
    }

    static fromProspectData(prospectData: ProspectDataDto): LinkedinProfileDto {
        let experiences: LinkedinProfileExperienceDto[] =
            prospectData.experiences?.map((experience) =>
                LinkedinProfileExperienceDto.fromProspectDataExperienceDto(experience),
            ) || [];
        const mainExperienceFromArray = experiences.find(
            (experience) =>
                (experience.companyName?.toLowerCase() === prospectData.organizationName?.toLowerCase() ||
                    experience.companyId?.toLowerCase() === prospectData.organizationId?.toLowerCase()) &&
                experience.jobTitle?.toLowerCase() === prospectData.occupationTitle?.toLowerCase(),
        );
        if (mainExperienceFromArray) {
            mainExperienceFromArray.isCurrent = true;
        } else {
            const mainExperienceSecondTry = experiences.find(
                (experience) =>
                    experience.companyName?.toLowerCase() === prospectData.organizationName?.toLowerCase() ||
                    experience.companyId?.toLowerCase() === prospectData.organizationId?.toLowerCase(),
            );
            if (mainExperienceSecondTry) {
                mainExperienceSecondTry.isCurrent = true;
            }
        }

        return new LinkedinProfileDto({
            memberId: prospectData.linkedinUserId as string,
            memberNumberId: undefined,
            publicIdentifier: extractPublicIdentifierFromLinkedinProfileUrl(prospectData.linkedinProfileUrl as string),
            firstName: prospectData.firstName,
            lastName: prospectData.lastName,
            fullName: prospectData.fullName,
            profileImageUrl: prospectData.linkedinProfileImgUrl,
            numberOfConnections: prospectData.linkedinNumberOfConnections,
            headline: prospectData.linkedinHeadline,
            personalSummary: prospectData.linkedinDescription,
            locationString: prospectData.location,
            email: prospectData.email && prospectData.email.includes('@') ? prospectData.email : undefined,
            phoneNumbers: prospectData.phoneNumbers,
            interests: prospectData.interests,
            skills: prospectData.skills,
            education: prospectData.education?.map(
                (education) =>
                    new LinkedinProfileEducationDto(
                        education.schoolName as string,
                        education.schoolLinkedinUrl as string,
                        education.schoolDomain as string,
                        education.majors as string[],
                        education.startDate as string,
                    ),
            ),
            experiences: experiences,
            personalIndustry: prospectData.industry,
            lastChecked: new Date(),
        });
    }

    static linkedinProfileUrlFromPublicIdentifier(publicIdentifier: string): string {
        return `https://www.linkedin.com/in/${publicIdentifier}`;
    }

    static toProspectDataDto(profile: LinkedinProfileDto): ProspectDataDto {
        const getCurrentExperience = (): LinkedinProfileExperienceDto | undefined => {
            return (
                profile.experiences?.find((experience) => experience.isCurrent) ||
                profile.experiences?.find((experience) => !experience.jobEndDate) ||
                profile.experiences?.[0]
            );
        };
        return {
            // lastCheckedAt: profile.lastChecked,
            firstName: profile.firstName,
            lastName: profile.lastName as string,
            fullName: profile.firstName + ' ' + (profile.lastName || ''),
            linkedinProfileUrl: LinkedinProfileDto.linkedinProfileUrlFromPublicIdentifier(
                profile.publicIdentifier ?? profile.memberId,
            ),
            linkedinProfileImgUrl: profile.profileImageUrl,
            linkedinUserId: profile.memberId,
            linkedinNumberOfConnections: profile.numberOfConnections,
            linkedinHeadline: profile.headline,
            linkedinDescription: profile.personalSummary,
            linkedinProfileVerification: {
                status: ProspectLinkedinProfileVerifiedStatus.verified,
                checkedAt: profile.lastChecked,
            },
            location: profile.locationString,
            occupationTitle: getCurrentExperience()?.jobTitle,
            occupationStartDate: getCurrentExperience()?.jobStartDate,
            organizationName: getCurrentExperience()?.companyName as string,
            organizationIndustry: getCurrentExperience()?.companyIndustry,
            organizationId: getCurrentExperience()?.companyPublicIdentifier,
            organizationLinkedinId: getCurrentExperience()?.companyId,
            organizationSize: getCurrentExperience()?.companySizeRange,
            organizationDomain: getDomainFromUrl(getCurrentExperience()?.companyWebsite),
            organizationLinkedinUrl: `https://www.linkedin.com/company/${
                getCurrentExperience()?.companyPublicIdentifier
            }`,
            experiences: profile.experiences?.map((experience) => {
                return {
                    id: experience.companyId as string,
                    organizationName: experience.companyName,
                    organizationId: experience.companyPublicIdentifier,
                    organizationLinkedinId: experience.companyId,
                    organizationLinkedinUrl: `https://www.linkedin.com/company/${experience.companyPublicIdentifier}`,
                    organizationDomain: getDomainFromUrl(experience.companyWebsite),
                    organizationIndustry: experience.companyIndustry,
                    organizationSize: experience.companySizeRange,
                    occupationTitle: experience.jobTitle,
                    occupationStartDate: experience.jobStartDate,
                    occupationEndDate: experience.jobEndDate,
                    occupationDescription: experience.summary,
                };
            }),
            email: profile.email,
            phoneNumbers: profile.phoneNumbers,
            interests: profile.interests,
            skills: profile.skills,
            education: profile.education,
        };
    }

    static getProfileStringSummary(profile: LinkedinProfileDto, maxNumberOfExperiences: number = 3): string {
        const parts: string[] = [];

        // Basic details
        if (profile.fullName) {
            parts.push(`Full Name: ${profile.fullName}`);
        }
        if (profile.numberOfConnections) {
            parts.push(`Connections: ${profile.numberOfConnections}`);
        }
        if (profile.headline) {
            parts.push(`Headline: ${profile.headline}`);
        }
        if (profile.locationString) {
            parts.push(`Location: ${profile.locationString}`);
        }
        if (profile.personalSummary) {
            parts.push(`Summary: ${truncateText(profile.personalSummary, 100)}`);
        }

        if (profile.experiences?.length) {
            parts.push('Experiences:');
            parts.push(
                ...LinkedinProfileDto.getProfileExperiencesStringSummary(
                    profile.experiences.slice(0, maxNumberOfExperiences),
                ),
            );
            if (profile.experiences?.length > maxNumberOfExperiences) {
                parts.push(`... and ${profile.experiences?.length - maxNumberOfExperiences} more`);
            }
        }

        // Education: include up to 2 items if relevant
        if (profile.education?.length) {
            const topEducation: LinkedinProfileEducationDto[] = profile.education.slice(0, 2);
            parts.push('Education:');
            topEducation.forEach((edu) => {
                parts.push(`- Institution: ${edu.schoolName}, Domain: ${edu.schoolDomain}, Start: ${edu.startDate}`);
            });
        }

        return parts.join('\n');
    }

    static getProfileExperiencesStringSummary(experiences: LinkedinProfileExperienceDto[]): string[] {
        const parts: string[] = [];
        experiences.forEach((exp) => {
            const experienceParts: string[] = [];
            if (exp.isCurrent) {
                experienceParts.push('  • Current: Yes');
            }
            if (exp.jobTitle) {
                experienceParts.push(`  • Role: ${exp.jobTitle}`);
            }
            if (exp.summary) {
                experienceParts.push(`  • Role Description: ${truncateText(exp.summary, 100)}`);
            }
            if (exp.companyName) {
                experienceParts.push(`  • Company: ${exp.companyName}`);
            }
            if (exp.companyIndustry) {
                experienceParts.push(`  • Industry: ${exp.companyIndustry}`);
            }
            if (exp.companySizeRange) {
                experienceParts.push(`  • Size: ${exp.companySizeRange}`);
            }
            if (exp.companyDescription) {
                experienceParts.push(`  • Company Description: ${truncateText(exp.companyDescription, 30)}`);
            }
            parts.push(`-\n${experienceParts.join('\n')}`);
        });
        return parts;
    }
}

function truncateText(text: string, maxWords: number): string {
    const words = text.split(' ');
    if (words.length > maxWords) {
        return words.slice(0, maxWords).join(' ').replace('\n', ' ') + ' ...';
    }
    return text.replace('\n', ' ');
}

function extractPublicIdentifierFromLinkedinProfileUrl(linkedinProfileUrl: string): string {
    return linkedinProfileUrl?.split(/\/in\/|\/company\//)?.[1]?.split('/')?.[0];
}

const getDomainFromUrl = (url?: string) => {
    if (!url) return undefined;
    const extractDomainRegex = /^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/\n]+)/im;
    const domain = url?.match(extractDomainRegex)?.[1];
    return domain;
};
